<template>
    <div class="flex flex-col h-full">
        <div class="flex justify-end mb-4">
            <apexo-input classes="w-64 bg-white" :placeholder="$t('orders.activations.search')" v-model="search" />
        </div>

        <div class="flex flex-col min-h-screen-1/3 max-h-screen-7/8 rounded-md shadow-lg bg-white overflow-hidden mb-4">
            <section class="flex-1 relative overflow-hidden">
                <div class="flex flex-col w-full h-full overflow-y-auto">
                    <apexo-table
                        :columns="columns"
                        :rows="formatedLicenses"
                        ref="body"
                        @onRowClick="showLicenseKey"
                    >
                        <template #no-results>
                            <p class="text-xl">
                                {{ $t('orders.activations.noActivations') }}
                            </p>
                        </template>
                    </apexo-table>
                </div>
            </section>

            <section class="flex justify-between items-center border-t border-gray-200 bg-white p-3 text-sm px-4" v-if="pagination.total">
                <pagination-info
                    :current="pagination.currentPage"
                    :paginated-item-name="$t('orders.activations.activations')"
                    :per-page="perPage"
                    :total="pagination.total"
                />

                <pagination
                    :current-page="pagination.currentPage"
                    :last-page="pagination.lastPage"
                    @update="setCurrentPage"
                    v-if="pagination.total > perPage"
                />
            </section>
        </div>

        <license-modal name="history-license-key" />
    </div>
</template>

<script>
    import debounce from 'lodash/debounce.js';
    import ApexoInput from '@/components/Elements/ApexoInput.vue';
    import ApexoTable from '@/components/Elements/ApexoTable.vue';
    import LicenseModal from '@/components/Activation/LicenseModal.vue';
    import Pagination from '@/components/Pagination.vue';
    import PaginationInfo from '@/components/PaginationInfo.vue';

    export default {
        components: {
            ApexoInput,
            ApexoTable,
            LicenseModal,
            Pagination,
            PaginationInfo,
        },

        props: {
            order: {
                type: Object,
                default: null,
            },
        },

        data() {
            return {
                search: '',
                activatedLicenses: [],
                perPage: 25,
                pagination: {
                    from: 0,
                    to: 0,
                    total: 0,
                    currentPage: 1,
                    lastPage: 0,
                },
            };
        },

        computed: {
            formatedLicenses() {
                return this.activatedLicenses
                    .map(license => {
                        return {
                            type: this.getLicenseTypeName(license.licenseType),
                            date: this.formatDateTime(license.activation_date),
                            deviceSerial: license.device.device_serial_number || '-',
                            firmwareVersion: license.device.firmware_version || '-',
                            afsVersion: license.device.afs_version || '-',
                            licenseKey: license.key,
                        };
                    });
            },

            columns() {
                return [
                    {
                        name: 'type',
                        label: this.$t('activations.list.type'),
                        icon: 'regular/single-neutral',
                        className: 'flex-3',
                    },
                    {
                        name: 'deviceSerial',
                        label: this.$t('activations.list.deviceSerialNumber'),
                        icon: 'regular/envelope',
                        className: 'flex-2',
                    },
                    {
                        name: 'firmwareVersion',
                        label: this.$t('activations.list.firmwareVersion'),
                        icon: 'regular/earth-1',
                        className: 'flex-1 text-center justify-center',
                    },
                    {
                        name: 'afsVersion',
                        label: this.$t('activations.list.afsVersion'),
                        icon: 'regular/earth-1',
                        className: 'flex-1 text-center justify-center',
                    },
                    {
                        name: 'date',
                        label: this.$t('activations.list.date'),
                        icon: 'regular/phone',
                        className: 'flex-2 justify-end',
                    },
                ];
            },
        },

        watch: {
            search() {
                this.debounceFetch();
            },
        },

        methods: {
            async debounceFetch() {
                await this.fetch();
            },

            async fetch() {
                this.$wait.start('fetching.activations');

                const payload = {
                    activated: true,
                    page: this.pagination.currentPage,
                    perPage: this.perPage,
                    search: this.search,
                };

                if (this.order) {
                    payload.order_id = this.order.id;
                }

                try {
                    const response = await this.$axios.get('licenses', {
                        params: payload,
                    });

                    this.activatedLicenses = response.data.data;

                    this.pagination.from = response.data.meta.from;
                    this.pagination.to = response.data.meta.to;
                    this.pagination.total = response.data.meta.total;
                    this.pagination.lastPage = response.data.meta.last_page;
                } catch (error) {
                    console.log('Error fetching activations');
                    console.log(error);
                } finally {
                    this.$wait.end('fetching.activations');
                }
            },

            getLicenseTypeName(licenseType) {
                const name = licenseType.localized_name[this.$i18n.locale];
                const platform = this.$t(`global.platform.${licenseType.platform}`);

                return `${name} (${platform})`;
            },

            showLicenseKey(row) {
                this.$modal.open('history-license-key', { licenseKey: row.licenseKey });
            },

            setCurrentPage(page) {
                this.pagination.currentPage = page;

                this.fetch();
            },

            onLicenseActivated(license) {
                if (this.search && !license.device.device_serial_number.includes(this.search)) {
                    return;
                }

                this.activatedLicenses.unshift(license);

                this.pagination.total++;
            },
        },

        created() {
            this.fetch();

            this.debounceFetch = debounce(this.debounceFetch, 300);
        },
    };
</script>
